import React from "react";
import { Info, Route, Save, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { EmailRouteButton } from "../EmailRouteButton";
import { buildGoogleMapsRouteUrl } from "../../lib/routes";
import { MAX_ROUTE_STOPS } from "./mapHelpers";

/**
 * Floating route panel for the Map page. Bottom-left overlay listing
 * selected stops, the depot bookend toggle, Plan/Email/Save actions.
 * Purely presentational — state + handlers come from the parent.
 */
export function RoutePanel({
  selected,
  clearSelection,
  removeStop,
  planRoute,
  saveAsLeadList,
  savingList,
  bookendDepot,
  setBookendDepot,
  depot,
}) {
  const canPlan =
    selected.length >= 1 &&
    (bookendDepot && depot ? true : selected.length >= 2);

  const showEmail =
    selected.length >= 2 || (bookendDepot && depot && selected.length >= 1);

  return (
    <div
      data-testid="map-route-panel"
      className="absolute bottom-4 left-4 panel p-3 bg-zinc-950 border border-emerald-500/40 w-80 space-y-2 shadow-xl ring-1 ring-zinc-900/60"
      style={{ zIndex: 1000 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-emerald-300">
          <Route className="w-3 h-3" />
          Route plan · {selected.length}/{MAX_ROUTE_STOPS}
        </div>
        {selected.length > 0 && (
          <button
            data-testid="route-clear-btn"
            onClick={clearSelection}
            className="text-[10px] font-mono text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {selected.length === 0 ? (
        <div
          data-testid="route-empty-state"
          className="text-xs text-zinc-400 leading-relaxed bg-emerald-500/5 border border-emerald-500/20 rounded-md p-2.5"
        >
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-zinc-200 font-medium mb-0.5">
                Build a route in 3 taps
              </div>
              <ol className="list-decimal list-inside space-y-0.5 text-zinc-400">
                <li>Click any marker on the map</li>
                <li>
                  Press{" "}
                  <span className="font-mono text-emerald-300">
                    Add to route
                  </span>{" "}
                  in the popup
                </li>
                <li>
                  {bookendDepot && depot ? (
                    <>
                      Add 1–9 stops, then{" "}
                      <span className="font-mono text-emerald-300">
                        Plan in Google Maps
                      </span>
                    </>
                  ) : (
                    <>
                      Repeat for up to 9 stops, then{" "}
                      <span className="font-mono text-emerald-300">
                        Plan in Google Maps
                      </span>
                    </>
                  )}
                </li>
              </ol>
              {bookendDepot && depot && (
                <div className="mt-1.5 text-[10px] text-emerald-300/80">
                  Depot bookend on — 1 stop becomes Depot → Stop → Depot.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <ol className="space-y-1 text-xs max-h-44 overflow-y-auto pr-1">
          {selected.map((s, i) => (
            <li
              key={s.id}
              data-testid={`route-stop-${s.id}`}
              className="flex items-center gap-2 group"
            >
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono shrink-0">
                {i + 1}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block truncate text-zinc-100">{s.name}</span>
                <span className="block truncate text-[10px] text-zinc-500 font-mono">
                  {s.city}
                </span>
              </span>
              <button
                data-testid={`route-remove-${s.id}`}
                onClick={() => removeStop(s)}
                className="text-zinc-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove stop"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ol>
      )}

      <Button
        data-testid="plan-route-btn"
        onClick={planRoute}
        disabled={!canPlan}
        className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-emerald-50 rounded-md text-xs h-8"
      >
        <Route className="w-3.5 h-3.5 mr-1.5" />
        {selected.length === 0
          ? "Plan route in Google Maps"
          : bookendDepot && depot
          ? `Plan depot → ${selected.length} stop${
              selected.length === 1 ? "" : "s"
            } → depot`
          : selected.length < 2
          ? "Add 1 more stop"
          : `Plan ${selected.length}-stop route in Google Maps`}
      </Button>

      {/* Bus-depot bookend toggle — persisted in localStorage by the parent. */}
      <label
        data-testid="map-bookend-depot-row"
        className="flex items-start gap-2 text-[11px] text-zinc-300 cursor-pointer select-none"
      >
        <input
          data-testid="map-bookend-depot-toggle"
          type="checkbox"
          checked={bookendDepot}
          onChange={(e) => setBookendDepot(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 accent-emerald-500"
        />
        <span className="flex-1 leading-snug">
          Start &amp; end at bus depot
          <span className="block text-[10px] font-mono text-zinc-500 truncate">
            {depot
              ? depot.address ||
                `${depot.latitude.toFixed(3)}, ${depot.longitude.toFixed(3)}`
              : "Set the depot address in Settings →"}
          </span>
        </span>
      </label>

      {showEmail && (
        <EmailRouteButton
          testIdPrefix="map-email"
          getSubject={() => {
            const n = selected.length;
            return bookendDepot && depot
              ? `JackTrack route — Depot → ${n} stop${
                  n === 1 ? "" : "s"
                } → Depot`
              : `JackTrack ${n}-stop route`;
          }}
          getMessage={() => {
            const url = buildGoogleMapsRouteUrl(selected, {
              depot: bookendDepot && depot ? depot : null,
            });
            const n = selected.length;
            const label =
              bookendDepot && depot
                ? `Depot → ${n} stop${n === 1 ? "" : "s"} → Depot`
                : `${n}-stop`;
            return `${label} JackTrack route:\n${url}`;
          }}
        />
      )}

      {selected.length > 0 && (
        <Button
          data-testid="save-as-lead-list-btn"
          onClick={saveAsLeadList}
          disabled={savingList}
          variant="outline"
          className="w-full border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-200 rounded-md text-xs h-8"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {savingList ? "Saving…" : "Save as lead list"}
        </Button>
      )}
    </div>
  );
}
